/**
 * Utilitaires pour gérer les couleurs des catégories de formations
 * Supporte un cache dynamique depuis la DB avec fallback hardcodé
 */

import { ref } from 'vue'
import { formationCategoriesService } from '../services/formationCategoriesService.js'

// Fallback hardcodé des catégories vers leurs couleurs
export const categoryColors = {
  'INCENDIE': '#dc3545', // Rouge
  'SST': '#28a745', // Vert
  'AFGSU': '#20c997', // Vert turquoise
  'PSC SECOURISME': '#17a2b8', // Vert bleu (cyan)
  'HABILITATION ELECTRIQUE': '#007bff', // Bleu
  'GESTES ET POSTURES': '#fd7e14', // Orange
  'HACCP': '#6f42c1', // Violet
  'CSE CHSCT': '#e83e8c', // Rose
  'TRAVAIL EN HAUTEUR': '#ffc107', // Jaune
  'TERRORISTE PPMS': '#6610f2', // Indigo
  'PPMS / CONFINEMENT / TERRORISTE': '#6610f2', // Indigo
  'CACES': '#20c997', // Vert turquoise
  'SSIAP CQPAPS': '#17a2b8', // Cyan
  'AIPR': '#6c757d', // Gris
  'CATEC': '#343a40', // Gris foncé
  'ATEX': '#fd7e14', // Orange
  'RPS QUALITÉ DE VIE AU TRAVAIL': '#e83e8c', // Rose
  'RISQUE CHIMIQUE': '#dc3545' // Rouge
}

// Fallback couleurs de fond
export const categoryBackgroundColors = {
  'INCENDIE': '#f8d7da', // Rouge clair
  'SST': '#d4edda', // Vert clair
  'AFGSU': '#d1ecf1', // Vert turquoise clair
  'PSC SECOURISME': '#d1ecf1', // Cyan clair
  'HABILITATION ELECTRIQUE': '#cce5ff', // Bleu clair
  'GESTES ET POSTURES': '#ffe5cc', // Orange clair
  'HACCP': '#e2d9f3', // Violet clair
  'CSE CHSCT': '#f8d7e8', // Rose clair
  'TRAVAIL EN HAUTEUR': '#fff3cd', // Jaune clair
  'TERRORISTE PPMS': '#e2d9f3', // Indigo clair
  'PPMS / CONFINEMENT / TERRORISTE': '#e2d9f3', // Indigo clair
  'CACES': '#d1ecf1', // Vert turquoise clair
  'SSIAP CQPAPS': '#d1ecf1', // Cyan clair
  'AIPR': '#e2e3e5', // Gris clair
  'CATEC': '#d6d8db', // Gris foncé clair
  'ATEX': '#ffe5cc', // Orange clair
  'RPS QUALITÉ DE VIE AU TRAVAIL': '#f8d7e8', // Rose clair
  'RISQUE CHIMIQUE': '#f8d7da' // Rouge clair
}

// Cache réactif depuis la DB
const dbCategoryMap = ref(new Map())

/**
 * Charge les couleurs de catégories depuis la DB
 */
export async function loadCategoryColorsFromDB() {
  try {
    const result = await formationCategoriesService.list()
    const categories = result.categories || []
    const map = new Map()
    categories.forEach(cat => {
      map.set(cat.nom.toUpperCase(), {
        color: cat.color,
        backgroundColor: cat.backgroundColor
      })
    })
    dbCategoryMap.value = map
  } catch (error) {
    console.warn('Could not load category colors from DB, using fallback:', error)
  }
}

/**
 * Rafraîchit les couleurs depuis la DB
 */
export async function refreshCategoryColors() {
  await loadCategoryColorsFromDB()
}

/**
 * Récupère la couleur d'une catégorie
 * @param {string} category - Le nom de la catégorie
 * @returns {string} La couleur hexadécimale
 */
export function getCategoryColor(category) {
  if (!category) return '#6c757d' // Gris par défaut
  const key = category.toUpperCase()
  const dbEntry = dbCategoryMap.value.get(key)
  if (dbEntry) return dbEntry.color
  return categoryColors[key] || '#6c757d'
}

/**
 * Récupère la couleur de fond d'une catégorie
 * @param {string} category - Le nom de la catégorie
 * @returns {string} La couleur hexadécimale
 */
export function getCategoryBackgroundColor(category) {
  if (!category) return '#e2e3e5' // Gris clair par défaut
  const key = category.toUpperCase()
  const dbEntry = dbCategoryMap.value.get(key)
  if (dbEntry) return dbEntry.backgroundColor
  return categoryBackgroundColors[key] || '#e2e3e5'
}

/**
 * Récupère le style pour un badge de catégorie
 * @param {string} category - Le nom de la catégorie
 * @returns {Object} Objet de style CSS
 */
export function getCategoryBadgeStyle(category) {
  return {
    color: getCategoryColor(category),
    backgroundColor: getCategoryBackgroundColor(category),
    borderColor: getCategoryColor(category)
  }
}
